<template>
  <component
    :is="as"
    tabindex="0"
    :class="[
      $style.tag,
      {
        [$style.disabled]: disabled,
        [$style.selected]: selected,
        [$style.error]: error,
        [$style.unavailable]: unavailable,
        [$style.avatar]: avatar,
        [$style.iconLeft]: slots['iconLeft'],
        [$style.iconRight]: removable || slots['iconRight'],
        [$style.variantUnderline]: variant === 'underline',
        [$style.variantBreadcrumb]: variant === 'breadcrumb',
        [$style.variantGhost]: variant === 'ghost',
      },
    ]"
    @click="updateSelected"
  >
    <SCAAvatar
      v-if="avatar"
      size="x3small"
      :initials="avatar?.initials"
      :shape="avatar?.shape"
      :appearance="avatar?.appearance"
      :emphasis="avatar?.emphasis"
      :bordered="!!avatar?.image"
      :color-idx="avatar?.colorIdx"
      :use-hover-colors="true"
    >
      <img v-if="avatar?.image" :src="avatar?.image" loading="lazy" alt="avatar" />
    </SCAAvatar>
    <SCASvgIcon v-if="icon" :class="$style.leadingIcon" :icon="icon" :size="iconSize" :color="iconColor" />
    <div v-if="slots['iconLeft']" :class="$style.iconContainer">
      <slot name="iconLeft" />
    </div>
    <SCASvgIcon
      v-if="variant === 'breadcrumb'"
      size="12px"
      :class="$style.breadcrumbIcon"
      icon="phosphor/caret-right"
    />
    <div ref="contentRef" :class="$style.content"><slot /></div>
    <div v-if="slots['iconRight']" :class="$style.iconContainer">
      <slot name="iconRight" />
    </div>
    <SCASvgIcon
      v-if="removable"
      :class="$style.button"
      size="12px"
      icon="phosphor/x"
      @click.stop="emit('remove')"
    ></SCASvgIcon>
    <SCATooltip
      v-if="tooltip || (truncateTooltip && isTruncated)"
      :target="contentRef"
      :text="tooltip ?? truncatedText"
    />
  </component>
</template>

<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import type { Component } from "vue";
import { computed, ref, useSlots } from "vue";

import SCAAvatar from "./SCAAvatar.vue";
import SCASvgIcon from "./SCASvgIcon.vue";
import SCATooltip from "./SCATooltip.vue";
import type { SCATagProps } from "./types";

// Kept out of SCATagProps on purpose: InputTagItem (inputTagsTypes.ts) also extends SCATagProps to
// describe plain tag data, and doesn't need this render-only prop. Merging `as` into SCATagProps
// broke the real `vite build` (its declaration bundling) for useInputTagsCore.ts — `vue-tsc` alone
// won't catch that, only an actual build does.
interface SCATagRenderProps {
  as?: string | Component;
}

const props = withDefaults(defineProps<SCATagProps & SCATagRenderProps>(), {
  disabled: false,
  selected: false,
  removable: false,
  error: false,
  avatar: undefined,
  icon: undefined,
  iconSize: "12px",
  iconColor: undefined,
  variant: "default",
  overflow: "hidden",
  truncateTooltip: false,
  unavailable: false,
  tooltip: undefined,
  as: "div",
});

const overflowRef = computed(() => props.overflow);

const emit = defineEmits<{
  remove: [];
  "update:selected": [selected: boolean];
}>();

const slots: ReturnType<typeof useSlots> = useSlots();

const contentRef = ref<HTMLElement | null>(null);
const isTruncated = ref(false);
const truncatedText = ref("");

const isOverflowing = (el: Element) => el.scrollWidth > el.clientWidth;

const checkTruncation = () => {
  if (!props.truncateTooltip) return;
  const el = contentRef.value;
  if (!el) return;
  isTruncated.value = isOverflowing(el) || Array.from(el.querySelectorAll("*")).some(isOverflowing);
  truncatedText.value = el.textContent?.trim() ?? "";
};

useResizeObserver(contentRef, checkTruncation);

const updateSelected = () => {
  if (props.disabled) {
    return;
  }
  emit("update:selected", !props.selected);
};
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.tag {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-component-gap-xSmall);
  padding: 0 var(--space-component-padding-xSmall);
  height: var(--size-2xSmall);
  min-height: var(--size-2xSmall);
  @include FigmaTypoMixins.typo-body-medium;
  text-wrap: nowrap;
  overflow: v-bind(overflowRef);

  color: var(--sca-tag-text-color, var(--color-content-primary));
  background-color: var(--color-interaction-neutral-subtle-normal);
  border-radius: var(--border-radius-medium);
  letter-spacing: 0.24px;

  &.disabled {
    opacity: 0.5;
    background-color: var(--color-interaction-neutral-subtle-normal);
    cursor: default;
  }

  &:not(.disabled):has(.button) {
    &:active {
      color: var(--color-action-primary-active);
      background-color: var(--color-interaction-neutral-subtle-active);
    }

    &:hover {
      cursor: pointer;
      color: var(--color-action-primary-hover);
      background-color: var(--color-interaction-neutral-subtle-hover);
    }
  }

  &.unavailable {
    .leadingIcon {
      @include IconMixins.icon-color(var(--color-action-neutral-normal));
    }

    &:not(.disabled):has(.button) {
      &:hover,
      &:active {
        color: var(--sca-tag-text-color, var(--color-content-primary));
      }
    }
  }

  &.error {
    color: var(--color-action-destructive-normal);
    background-color: var(--color-background-negative-subtle);
    @include IconMixins.icon-color(currentColor);

    .button:hover {
      background-color: var(--color-background-negative-muted);
    }

    &:not(.disabled):has(.button) {
      &:hover,
      &:active {
        color: var(--color-action-destructive-hover);
        background-color: var(--color-background-negative-subtle);
      }
    }
  }

  &.variantUnderline {
    gap: var(--space-component-gap-2xSmall);
    color: var(--color-content-primary);
    background-color: unset;
    border-radius: unset;
    min-height: 20px;
    padding: var(--space-component-padding-null) 0px;

    &:not(.disabled) {
      &:active {
        background-color: unset;
      }

      &.selected {
        color: var(--color-action-primary-active);
        background-color: unset;

        .content {
          box-shadow: inset 0px -1px 0px 0px var(--color-action-primary-active);
        }
      }

      &:hover:not(.selected) {
        background-color: unset;
        box-shadow: none;
      }
    }

    &.disabled {
      background-color: unset;
    }
  }

  &.variantBreadcrumb {
    gap: 0;
    background-color: unset;
    border-radius: unset;
    min-height: 20px;
    padding: 0;
    color: var(--color-neutralGrey-p1);
    .content {
      padding: 0 var(--space-component-padding-xSmall);
    }

    svg {
      @include IconMixins.icon-color(var(--color-neutralGrey-p1));
    }

    // Remove the chevron icon of the first element
    &:first-child {
      .breadcrumbIcon {
        display: none;
      }
    }

    &:not(.disabled) {
      &:active {
        color: var(--color-action-primary-active);
        background-color: unset;
      }

      &:not(.selected) {
        &:focus,
        &:hover {
          cursor: pointer;
          color: var(--color-action-primary-hover);
          background-color: unset;
        }
      }
    }

    &.disabled {
      background-color: unset;
      opacity: 1;
    }
  }

  &.variantGhost {
    background-color: transparent;
    height: var(--dimension-350);
    padding: var(--space-component-padding-small);
  }

  &.avatar {
    padding-left: var(--space-component-padding-2xSmall);
  }
  &.iconLeft {
    padding-left: var(--space-component-padding-xSmall);
  }

  &.iconRight {
    padding-right: var(--space-component-padding-xSmall);
  }

  &:focus {
    outline: none;
  }

  .content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    flex-grow: 1;
  }

  .iconContainer {
    display: flex;
    align-items: center;
    min-width: 16px;
  }

  .button {
    padding: 0;
    @include IconMixins.icon-color(currentColor);

    &:hover {
      background-color: var(--color-background-neutral-muted);
      border-radius: 2px;
    }
  }
}
</style>
